import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useLazyQuery, useSubscription, gql } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { 
  ChevronDown, 
  ChevronUp, 
  Search, 
  RefreshCw, 
  AlertTriangle, 
  Bug, 
  Zap,
  Shield,
  Clock,
  User,
  Code,
  Globe,
  Database
} from 'lucide-react';

// GraphQL Queries
const GET_ERROR_LOG_STATS = gql`
  query GetErrorLogStats {
    errorLogStats {
      total_errors
      graphql_errors
      uncaught_errors
      unhandled_errors
      last_24h_errors
    }
  }
`;

const LIST_ERROR_LOGS = gql`
  query ListErrorLogs($limit: Int, $offset: Int, $error_type: String, $user_id: String) {
    listErrorLogs(limit: $limit, offset: $offset, error_type: $error_type, user_id: $user_id) {
      id
      error_message
      error_type
      stack_trace
      user_id
      username
      query
      variables
      operation_name
      timestamp
      ip_address
      user_agent
      session_id
    }
  }
`;

const ERROR_LOGS_SUBSCRIPTION = gql`
  subscription SubscriptionErrorLogUpdates {
    subscriptionErrorLogUpdates {
      id
      error_message
      error_type
      stack_trace
      user_id
      username
      query
      variables
      operation_name
      timestamp
      ip_address
      user_agent
      session_id
    }
  }
`;

interface ErrorLog {
  id: string;
  error_message: string;
  error_type: string;
  stack_trace?: string;
  user_id?: string;
  username?: string;
  query?: string;
  variables?: string;
  operation_name?: string;
  timestamp: string | number;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
}

interface ErrorStats {
  total_errors: number;
  graphql_errors: number;
  uncaught_errors: number;
  unhandled_errors: number;
  last_24h_errors: number;
}

const ErrorTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'graphql':
      return <Code className="h-4 w-4" />;
    case 'uncaught':
      return <Bug className="h-4 w-4" />;
    case 'unhandled':
      return <Zap className="h-4 w-4" />;
    default:
      return <AlertTriangle className="h-4 w-4" />;
  }
};

const ErrorTypeBadge = ({ type }: { type: string }) => {
  const variants = {
    graphql: 'destructive',
    uncaught: 'secondary',
    unhandled: 'outline'
  } as const;

  return (
    <Badge variant={variants[type as keyof typeof variants] || 'default'} className="flex items-center gap-1">
      <ErrorTypeIcon type={type} />
      {type.toUpperCase()}
    </Badge>
  );
};

export const ErrorLogs = () => {
  const [filters, setFilters] = useState({
    search: '',
    errorType: '',
    userId: '',
    timeRange: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    offset: 0
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filteredLogs, setFilteredLogs] = useState<ErrorLog[]>([]);
  const [newErrorIds, setNewErrorIds] = useState<Set<string>>(new Set());

  // Stats Query
  const { data: statsData, loading: statsLoading, refetch: refetchStats } = useQuery<{ errorLogStats: ErrorStats }>(GET_ERROR_LOG_STATS, {
    fetchPolicy: 'cache-and-network'
  });

  // Error Logs Query
  const [loadErrorLogs, { data: logsData, loading: logsLoading, error: logsError }] = useLazyQuery<{ listErrorLogs: ErrorLog[] }>(LIST_ERROR_LOGS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Real-time Error Logs Subscription
  const { data: subscriptionData, error: subscriptionError } = useSubscription<{ subscriptionErrorLogUpdates: ErrorLog }>(ERROR_LOGS_SUBSCRIPTION);

  const handleSearch = useCallback(() => {
    const variables: any = {
      limit: pagination.limit,
      offset: pagination.offset
    };

    if (filters.errorType) variables.error_type = filters.errorType;
    if (filters.userId) variables.user_id = filters.userId;

    loadErrorLogs({ variables });
  }, [filters.errorType, filters.userId, pagination.limit, pagination.offset, loadErrorLogs]);

  // Client-side filtering for search and time range
  useEffect(() => {
    if (!logsData?.listErrorLogs) {
      setFilteredLogs([]);
      return;
    }

    let logs = [...logsData.listErrorLogs];

    // Search filter
    if (filters.search) {
      logs = logs.filter(log => 
        log.error_message.toLowerCase().includes(filters.search.toLowerCase()) ||
        (log.operation_name && log.operation_name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (log.username && log.username.toLowerCase().includes(filters.search.toLowerCase()))
      );
    }

    // Time range filter
    if (filters.timeRange) {
      const now = new Date();
      const cutoffTime = new Date();
      
      switch (filters.timeRange) {
        case '1h':
          cutoffTime.setHours(now.getHours() - 1);
          break;
        case '6h':
          cutoffTime.setHours(now.getHours() - 6);
          break;
        case '24h':
          cutoffTime.setDate(now.getDate() - 1);
          break;
        case '7d':
          cutoffTime.setDate(now.getDate() - 7);
          break;
      }
      
      logs = logs.filter(log => {
        if (!log.timestamp) return false;
        
        let logDate: Date;
        if (typeof log.timestamp === 'string') {
          if (/^\d+$/.test(log.timestamp)) {
            const numTimestamp = parseInt(log.timestamp);
            logDate = new Date(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp);
          } else {
            logDate = new Date(log.timestamp);
          }
        } else if (typeof log.timestamp === 'number') {
          logDate = new Date(log.timestamp < 10000000000 ? log.timestamp * 1000 : log.timestamp);
        } else {
          return false;
        }
        
        return !isNaN(logDate.getTime()) && logDate >= cutoffTime;
      });
    }

    setFilteredLogs(logs);
  }, [logsData, filters.search, filters.timeRange]);

  // Handle real-time error log updates
  useEffect(() => {
    if (subscriptionData?.subscriptionErrorLogUpdates) {
      const newError = subscriptionData.subscriptionErrorLogUpdates;
      
      // Add new error to the beginning of the list
      setFilteredLogs(prevLogs => {
        // Check if error already exists to avoid duplicates
        const existingError = prevLogs.find(log => log.id === newError.id);
        if (existingError) {
          return prevLogs;
        }
        
        // Add new error at the beginning
        const updatedLogs = [newError, ...prevLogs];
        
        // Keep only the current page size to prevent infinite growth
        const maxSize = pagination.limit * 2; // Keep 2 pages worth of data
        return updatedLogs.slice(0, maxSize);
      });

      // Mark as new for visual highlighting
      setNewErrorIds(prev => {
        const newSet = new Set(prev);
        newSet.add(newError.id);
        return newSet;
      });
      
      // Remove new highlighting after 5 seconds
      setTimeout(() => {
        setNewErrorIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(newError.id);
          return newSet;
        });
      }, 5000);

      // Update statistics
      refetchStats();

      // Show toast notification for new errors
      const errorTypeColor = {
        graphql: '🔍',
        uncaught: '🚨',
        unhandled: '⚠️'
      }[newError.error_type] || '❌';
      
      toast.error(
        `${errorTypeColor} New ${newError.error_type} error: ${newError.error_message.substring(0, 100)}${newError.error_message.length > 100 ? '...' : ''}`,
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    }
  }, [subscriptionData, pagination.limit, refetchStats]);

  // Handle subscription errors
  useEffect(() => {
    if (subscriptionError) {
      console.error('Subscription error:', subscriptionError);
      toast.error('Real-time updates disconnected. Please refresh the page.', {
        position: 'top-right',
        autoClose: 10000,
      });
    }
  }, [subscriptionError]);

  // Initial load
  useEffect(() => {
    handleSearch();
  }, [handleSearch]);


  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1, offset: 0 }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    setPagination(prev => {
      const newPage = direction === 'prev' ? prev.page - 1 : prev.page + 1;
      return {
        ...prev,
        page: newPage,
        offset: (newPage - 1) * prev.limit
      };
    });
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      errorType: '',
      userId: '',
      timeRange: ''
    });
    setPagination({ page: 1, limit: 25, offset: 0 });
  };

  const formatTimestamp = (timestamp: string | number, includeSeconds = false) => {
    if (!timestamp) return 'N/A';
    
    // Debug: Log the timestamp to see what format it's in
    console.log('Timestamp received:', timestamp, typeof timestamp);
    
    let date: Date;
    
    // Try different parsing approaches
    if (typeof timestamp === 'string') {
      // If it's a Unix timestamp (number as string)
      if (/^\d+$/.test(timestamp)) {
        // Convert to number and handle both seconds and milliseconds
        const numTimestamp = parseInt(timestamp);
        date = new Date(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp);
      } else {
        // Regular date string
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      // Handle numeric timestamps
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else {
      return 'Invalid Date';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date created from timestamp:', timestamp);
      return `Invalid Date (${timestamp})`;
    }
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' })
    };
    
    try {
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatJSON = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const stats = statsData?.errorLogStats;

  return (
    <div className="p-4 w-full min-h-screen flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Shield className="h-8 w-8 text-red-500" />
          Error Logs Dashboard
        </h1>
        <p className="text-gray-600">Monitor and analyze application errors in real-time</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Total Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : stats?.total_errors || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">GraphQL Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : stats?.graphql_errors || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Uncaught Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : stats?.uncaught_errors || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Unhandled Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : stats?.unhandled_errors || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Last 24h</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : stats?.last_24h_errors || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <Input
                placeholder="Search error messages..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Error Type</label>
              <Select value={filters.errorType || "all"} onValueChange={(value) => handleFilterChange('errorType', value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="graphql">GraphQL</SelectItem>
                  <SelectItem value="uncaught">Uncaught</SelectItem>
                  <SelectItem value="unhandled">Unhandled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">User ID</label>
              <Input
                placeholder="Filter by user ID..."
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Time Range</label>
              <Select value={filters.timeRange || "all"} onValueChange={(value) => handleFilterChange('timeRange', value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="6h">Last 6 Hours</SelectItem>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <Button onClick={handleSearch} className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm font-medium text-gray-700">Per Page:</label>
              <Select 
                value={pagination.limit.toString()} 
                onValueChange={(value) => setPagination(prev => ({ ...prev, limit: parseInt(value), offset: 0, page: 1 }))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {logsError && (
        <Alert className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load error logs: {logsError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Logs Table */}
      <Card className="flex-1 flex flex-col">
        <CardHeader className="pb-3 flex-shrink-0">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span>Error Logs</span>
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Live</span>
              </div>
            </div>
            <span className="text-sm text-gray-500">
              {logsLoading ? 'Loading...' : `${filteredLogs.length} error(s) found`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 flex-1 flex flex-col">
          <div className="overflow-x-auto flex-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Error Message</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Operation</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading error logs...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12">
                      <EmptyState 
                        icon={Database}
                        title="No error logs found"
                        description="Try adjusting your filters or search terms."
                        action={
                          <Button variant="outline" size="sm" onClick={clearFilters}>
                            Clear All Filters
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow className={`hover:bg-gray-50 ${newErrorIds.has(log.id) ? 'bg-yellow-50 border-l-4 border-yellow-400 animate-pulse' : ''}`}>
                        <TableCell className="font-mono text-sm">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <ErrorTypeBadge type={log.error_type} />
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate" title={log.error_message}>
                            {truncateText(log.error_message, 60)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-gray-400" />
                            {log.username || log.user_id || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {log.operation_name || 'N/A'}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-1">
                            <Globe className="h-3 w-3 text-gray-400" />
                            {log.ip_address || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRowExpansion(log.id)}
                            className="flex items-center gap-1"
                          >
                            {expandedRows.has(log.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                      
                      {expandedRows.has(log.id) && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-gray-50 border-t">
                            <div className="p-3 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2">Error Details</h4>
                                  <div className="bg-white p-3 rounded border text-sm">
                                    <strong>Full Error Message:</strong>
                                    <p className="mt-1 text-gray-700">{log.error_message}</p>
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2">Request Information</h4>
                                  <div className="bg-white p-3 rounded border text-sm space-y-2">
                                    <div><strong>Timestamp:</strong> {formatTimestamp(log.timestamp, true)}</div>
                                    <div><strong>Session ID:</strong> {log.session_id || 'N/A'}</div>
                                    <div><strong>User Agent:</strong> {log.user_agent ? truncateText(log.user_agent, 80) : 'N/A'}</div>
                                  </div>
                                </div>
                              </div>

                              {log.query && (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                                    <Code className="h-4 w-4" />
                                    GraphQL Query
                                  </h4>
                                  <pre className="bg-gray-900 text-gray-100 p-3 rounded text-sm overflow-x-auto">
                                    {log.query}
                                  </pre>
                                </div>
                              )}

                              {log.variables && (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2">Variables</h4>
                                  <pre className="bg-gray-900 text-gray-100 p-3 rounded text-sm overflow-x-auto">
                                    {formatJSON(log.variables)}
                                  </pre>
                                </div>
                              )}

                              {log.stack_trace && (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                                    <Bug className="h-4 w-4" />
                                    Stack Trace
                                  </h4>
                                  <pre className="bg-red-50 text-red-800 p-3 rounded text-sm overflow-x-auto max-h-64 overflow-y-auto border border-red-200">
                                    {log.stack_trace}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-4 mt-4 pb-4">
        <Button
          variant="outline"
          onClick={() => handlePageChange('prev')}
          disabled={pagination.page <= 1}
        >
          ← Previous
        </Button>
        <span className="text-sm text-gray-600">
          Page {pagination.page} • Showing {filteredLogs.length} result(s)
        </span>
        <Button
          variant="outline"
          onClick={() => handlePageChange('next')}
          disabled={filteredLogs.length < pagination.limit}
        >
          Next →
        </Button>
      </div>
    </div>
  );
};